import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import CountUp from "react-countup";
import { STATS_HERO, WHY_US, HERO_IMG, LOGO_SRC } from "@/constants/site";
import { LineMask, KineticText, Rise } from "@/components/Motion";
import Marquee from "@/components/Marquee";
import SEO, { ORG_JSONLD } from "@/components/SEO";

const MANIFESTO = [
  {
    n: "01",
    title: "Mentors first, curriculum second.",
    body: "Every batch is anchored by teachers who have appeared for the exam themselves. You learn strategy, not just syllabus.",
  },
  {
    n: "02",
    title: "One institute. Every path.",
    body: "School coaching, NEET, JEE, UPSC, MPSC, languages, IT — one campus, one team, one culture of excellence.",
  },
  {
    n: "03",
    title: "Small cohorts. Real attention.",
    body: "We cap seats so every student gets one-on-one doubt sessions, weekly assessments and a personal mentor.",
  },
  {
    n: "04",
    title: "Discipline is a signature move.",
    body: "Rituals over motivation — daily practice, timed tests, reflection journals. That's how confident students are made.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.15]);

  return (
    <div>
      <SEO
        title="AM Group of Education | School, UPSC, MPSC, NEET, JEE & Language Coaching"
        description="Premium coaching institute for Classes 1–12 (SSC, CBSE, ICSE), NEET, JEE, UPSC, MPSC, government exams, foreign languages and IT courses under one roof."
        path="/"
        jsonLd={[ORG_JSONLD]}
      />
      {/* ============================================================ */}
      {/* HERO — kinetic reveal + parallax                              */}
      {/* ============================================================ */}
      <section ref={heroRef} className="relative min-h-[100vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Parallax background */}
        <motion.div style={{ y: bgY }} className="absolute inset-0 will-change-transform">
          <img src={HERO_IMG} alt="Modern classroom with engaged students" className="absolute inset-0 w-full h-full object-cover scale-[1.15]" />
        </motion.div>

        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-dark/92 via-brand-primary/70 to-brand-dark/92" />
        <div className="absolute inset-0 bg-brand-ink/30" />
        {/* Grain */}
        <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none" style={{
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }} />

        {/* Signature framing lines */}
        <div className="absolute inset-x-0 top-24 h-px bg-brand-gold/25" />
        <div className="absolute inset-x-0 bottom-24 h-px bg-brand-gold/25" />

        {/* Content */}
        <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          {/* Chapter mark */}
          <div className="flex items-center gap-4 text-brand-gold text-xs uppercase tracking-[0.35em] font-semibold">
            <LineMask delay={0.1}>
              <span>Chapter 00 — The Institute</span>
            </LineMask>
            <span className="flex-1 h-px bg-brand-gold/30 max-w-[220px]" />
          </div>

          {/* Kinetic H1 */}
          <div className="mt-6 flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-12">
            <motion.img
              src={LOGO_SRC}
              alt="AM Group of Education Logo"
              initial={{ opacity: 0, scale: 0.6, rotate: -18 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
              className="h-40 sm:h-48 lg:h-56 w-auto object-contain drop-shadow-2xl animate-float-slow shrink-0"
            />
            <h1 className="display-serif text-5xl sm:text-7xl lg:text-8xl xl:text-[7.5rem] font-extrabold text-white leading-[0.95] tracking-tight drop-shadow-2xl">
              <LineMask delay={0.2}><span className="block">Welcome to</span></LineMask>
              <LineMask delay={0.4} className="mt-1">
                <span className="block bg-gradient-to-r from-brand-gold via-yellow-100 to-brand-gold bg-clip-text text-transparent">AM Group</span>
              </LineMask>
              <LineMask delay={0.55} className="mt-1">
                <span className="block bg-gradient-to-r from-brand-gold via-yellow-100 to-brand-gold bg-clip-text text-transparent italic">of Education</span>
              </LineMask>
            </h1>
          </div>

          {/* Sub */}
          <div className="mt-10 max-w-3xl">
            <p className="text-lg lg:text-2xl text-white/85 leading-relaxed">
              <KineticText delay={0.9} text="One institute. Every path — school, competitive exams, languages and IT — taught by mentors who genuinely care." />
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <Link to="/contact" className="btn-primary !text-base !px-10 !py-4" data-testid="hero-enroll-btn">
              Enroll Now <i className="fa-solid fa-arrow-right" />
            </Link>
            <Link to="/contact" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-brand-dark !text-base !px-10 !py-4" data-testid="hero-contact-btn">
              Contact Us
            </Link>
            <div className="hidden sm:flex items-center gap-3 text-white/70 text-xs uppercase tracking-[0.3em] ml-4">
              <span className="w-8 h-px bg-brand-gold" /> scroll
            </div>
          </motion.div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-brand-gold/80"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 0.8 }}
        >
          <span className="text-[10px] uppercase tracking-[0.4em] font-semibold">Discover</span>
          <motion.span className="w-px h-14 bg-brand-gold/60" animate={{ scaleY: [1, 0.3, 1] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }} />
        </motion.div>
      </section>

      {/* ============================================================ */}
      {/* STATISTICS — 4 cards immediately after hero                    */}
      {/* ============================================================ */}
      <section className="relative -mt-20 z-20 px-4 sm:px-6 lg:px-8" data-testid="stats-section">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {STATS_HERO.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -8 }}
              className="group relative overflow-hidden rounded-2xl bg-white border border-brand-line shadow-[0_25px_60px_-20px_rgba(78,52,46,.35)] p-5 sm:p-6"
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-brand-gold/10 blur-2xl group-hover:bg-brand-gold/30 transition-colors duration-500" />
              <div className="relative">
                <span className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-brand-primary to-brand-dark text-brand-gold grid place-items-center shadow-lg mb-3">
                  <i className={`fa-solid fa-${s.icon} text-xl sm:text-2xl`} />
                </span>
                <div className="display-serif text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-dark leading-none">
                  <CountUp end={s.value} duration={2.6} suffix={s.suffix} separator="," enableScrollSpy scrollSpyOnce />
                </div>
                <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-brand-mute mt-2 font-semibold">{s.label}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/* MARQUEE — editorial tape                                       */}
      {/* ============================================================ */}
      <div className="relative mt-24 border-y border-brand-line bg-brand-cream py-6 overflow-hidden">
        <Marquee
          duration={50}
          className="display-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark/90 italic tracking-tight"
          items={[
            "Learn Today",
            "Lead Tomorrow",
            "Mentors Who Care",
            "Small Batches",
            "Big Results",
            "Since Day One",
          ]}
        />
      </div>

      {/* ============================================================ */}
      {/* SPOKEN ENGLISH HIGHLIGHT (poster + copy)                       */}
      {/* ============================================================ */}
      <section className="relative py-24 bg-brand-cream" data-testid="spoken-english-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative grid lg:grid-cols-12 gap-10 items-center rounded-3xl p-8 sm:p-12 bg-white border border-brand-line shadow-[0_30px_80px_-40px_rgba(78,52,46,.45)] overflow-hidden">
            <div className="absolute -right-24 -bottom-24 w-[420px] h-[420px] rounded-full bg-brand-gold/10 blur-3xl" />
            <Rise className="lg:col-span-7 relative z-10">
              <span className="kicker">Premium Program</span>
              <h2 className="display-serif mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark">Experienced and Qualified Teachers</h2>
              <p className="mt-3 text-brand-mute">Learn from experts who care.</p>

              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {["Basic to Advanced","Grammar","Vocabulary","Public Speaking","Interview Preparation","Personality Development"].map((f, i) => (
                  <motion.li
                    key={f}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-center gap-3 text-brand-ink"
                  >
                    <span className="w-7 h-7 rounded-full bg-brand-gold/15 text-brand-primary grid place-items-center">
                      <i className="fa-solid fa-check text-xs" />
                    </span>
                    <span className="font-medium">{f}</span>
                  </motion.li>
                ))}
              </ul>

              <Link to="/contact" className="btn-primary mt-8" data-testid="spoken-english-join-btn">
                Meet Our Teachers <i className="fa-solid fa-arrow-right" />
              </Link>
            </Rise>
            <Rise delay={0.15} className="lg:col-span-5 relative z-10">
              <motion.div
                whileHover={{ scale: 1.02, rotate: -0.5 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="relative rounded-2xl overflow-hidden bg-brand-cream border border-brand-line shadow-[0_20px_60px_-25px_rgba(78,52,46,.5)]"
              >
                <img alt="Supercharge Your English — Online Course by AM Group of Education" className="w-full h-auto block" src="/brand/poster.jpg" />
                {/* Spotlight overlay */}
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,transparent_0%,transparent_45%,rgba(42,30,24,.35)_100%)]" />
                <div className="absolute top-4 left-4 bg-brand-dark/90 backdrop-blur-md text-brand-gold rounded-full px-3 py-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest">
                  <i className="fa-solid fa-microphone" /> Live Online Course
                </div>
              </motion.div>
            </Rise>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* MANIFESTO — numbered chapters                                  */}
      {/* ============================================================ */}
      <section className="relative py-28 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <Rise>
                <span className="kicker">Our Manifesto</span>
                <h2 className="display-serif mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-brand-dark leading-[1.02] max-w-3xl">
                  Four beliefs that <span className="italic text-brand-primary">shape</span> every batch.
                </h2>
              </Rise>
            </div>
            <Rise delay={0.15}>
              <Link to="/about" className="btn-outline">Read the story <i className="fa-solid fa-arrow-right" /></Link>
            </Rise>
          </div>

          <div className="mt-16 grid lg:grid-cols-2 gap-x-16 gap-y-6">
            {MANIFESTO.map((m, i) => (
              <motion.article
                key={m.n}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, delay: (i % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="group relative py-8 border-t border-brand-line/70"
              >
                <div className="flex items-baseline gap-6">
                  <span className="display-serif text-5xl sm:text-6xl font-extrabold text-brand-gold/70 tabular-nums">{m.n}</span>
                  <div className="flex-1">
                    <h3 className="display-serif text-2xl sm:text-3xl font-bold text-brand-dark leading-tight group-hover:text-brand-primary transition-colors">{m.title}</h3>
                    <p className="mt-3 text-brand-mute leading-relaxed max-w-xl">{m.body}</p>
                  </div>
                  <motion.i
                    className="fa-solid fa-arrow-right text-brand-primary opacity-0 group-hover:opacity-100 transition-opacity mt-2"
                    initial={{ x: -10 }}
                    whileHover={{ x: 0 }}
                  />
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* FOUNDER PHOTO — placed directly below Manifesto                */}
      {/* ============================================================ */}
      <section className="relative py-16 sm:py-24 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-3xl overflow-hidden shadow-[0_40px_100px_-30px_rgba(78,52,46,.5)] border border-brand-line bg-brand-cream"
          >
            <img
              src="/brand/founder-office.webp"
              alt="Arif Mulani — Founder & CEO of AM Group of Education at his office desk"
              className="w-full h-auto block object-cover"
              loading="lazy"
              draggable={false}
            />
            {/* Elegant gradient overlay on the bottom for legibility */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-brand-dark/85 via-brand-dark/40 to-transparent" />
            {/* Text label */}
            <div className="absolute bottom-6 sm:bottom-10 left-6 sm:left-10 right-6 text-white">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] font-semibold text-brand-gold">Founder &amp; CEO</div>
              <div className="display-serif mt-1 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">Arif Mulani</div>
            </div>
            {/* Golden accent bar */}
            <div className="absolute bottom-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-brand-gold to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* WHY CHOOSE US — 8 pillars                                      */}
      {/* ============================================================ */}
      <section className="py-20 bg-brand-cream" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Rise>
              <span className="kicker mx-auto">Why Choose Us</span>
              <h2 className="display-serif mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark">Built for Results, Designed for Care.</h2>
              <p className="mt-3 text-brand-mute">Eight pillars that make AM Group of Education a trusted name across Maharashtra.</p>
            </Rise>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_US.map((w, i) => (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.75, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6 }}
                className="rounded-2xl border border-brand-line bg-white p-6"
              >
                <span className="w-12 h-12 rounded-xl bg-brand-primary text-brand-gold grid place-items-center">
                  <i className={`fa-solid fa-${w.icon} text-lg`} />
                </span>
                <h3 className="mt-4 font-semibold text-brand-dark">{w.title}</h3>
                <p className="mt-2 text-sm text-brand-mute leading-relaxed">{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* CTA strip                                                     */}
      {/* ============================================================ */}
      <section className="relative py-20 bg-brand-dark text-white overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[420px] h-[420px] rounded-full bg-brand-gold/15 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8 items-center relative">
          <Rise className="lg:col-span-2">
            <h3 className="display-serif text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.05]">
              Ready to start your journey <br className="hidden lg:block" />with mentors who <span className="italic text-brand-gold">care</span>?
            </h3>
          </Rise>
          <Rise delay={0.15} className="flex gap-3 lg:justify-end flex-wrap">
            <Link to="/courses" className="btn-outline !border-brand-gold !text-brand-gold hover:!bg-brand-gold hover:!text-brand-dark">Browse Courses</Link>
            <Link to="/contact" className="btn-primary !bg-brand-gold !text-brand-dark hover:!bg-white">Book Demo</Link>
          </Rise>
        </div>
      </section>
    </div>
  );
}
